import React, { useCallback, useEffect, useState } from 'react'
import PropTypes, { string, shape, func, bool } from 'prop-types'
import { Form, Input, message } from 'antd'
import InfoField from '~/components/infoField'
import { useFocusCallback } from 'utils/fieldHooks'
import { getNubankEmployees } from 'services/nubank'
import useDebounce from 'hooks/useDebounce'

const EmailField = ({
	pageFieldsData,
	inputValue,
	className,
	onChange,
	onFocus,
	first,
	placeHolderRequirement,
	OutsideLabel,
	disabled,
	visible,
	form,
	setRequesterData = () => {},
}) => {
	const {
		label,
		variable,
		type,
		id,
		info,
		list,
		optional,
		hasNubankIntegration = false,
		autofillFields = [],
		parentField = '',
	} = pageFieldsData
	const isObj = typeof variable === 'object'
	const varname = isObj ? variable.name : variable
	const name = id !== undefined ? `${varname}_${id}` : varname

	const [fieldValue, setFieldValue] = useState(inputValue)
	const debouncedFieldValue = useDebounce(fieldValue, 500)

	useEffect(() => {
		handleNubankFetch()
	}, [debouncedFieldValue])

	const handleNubankFetch = useCallback(async () => {
		// Avoid making the API call if the value hasn't changed
		if (inputValue === debouncedFieldValue) return

		if (
			hasNubankIntegration &&
			debouncedFieldValue?.endsWith('@nubank.com.br')
		) {
			const hide = message.loading('Buscando dados no Nubank...', 0)

			const email = debouncedFieldValue.trim()

			try {
				const data = await getNubankEmployees(email)

				if (varname === 'SOLICITANTE_EMAIL') {
					setRequesterData(data)
				}

				const fieldsToFill = autofillFields
					? Object.entries(autofillFields)
					: []

				fieldsToFill.forEach(([field, valueKey]) => {
					if (list !== -1 && parentField) {
						form.setFieldValue([parentField, list, field], data[valueKey])
						return
					}

					form.setFieldValue(field, data[valueKey])
				})
			} catch (error) {
				console.error('Erro ao buscar dados no Nubank:', error)
				message.error(
					'Não foi possível buscar os dados no Nubank. Verifique se o e-mail está correto e tente novamente.'
				)
			} finally {
				hide()
			}
		}
	}, [hasNubankIntegration, autofillFields, debouncedFieldValue, form])

	const returnLabel = () => {
		if (label.length > 0) {
			return <InfoField label={label} info={info} />
		} else if (OutsideLabel.length > 0) {
			return <InfoField label={OutsideLabel} info={info} />
		}
		return null
	}

	const changeCallback = useCallback(
		(e) => {
			setFieldValue(e.target.value)
			if (onChange) onChange(e.target.value)
		},
		[onChange]
	)

	const focusCallback = useFocusCallback(onFocus, pageFieldsData)

	return (
		<Form.Item
			key={name}
			name={list !== undefined ? [list, name] : name}
			label={returnLabel()}
			type={type}
			className={className}
			hasFeedback
			rules={
				visible && [
					{ type, message: 'E-mail não é válido.' },
					{ required: !optional, message: 'Este campo é obrigatório.' },
				]
			}
			colon={false}
			initialValue={!inputValue ? '' : inputValue}>
			<Input
				onChange={changeCallback}
				onFocus={focusCallback}
				autoFocus={first}
				placeholder={placeHolderRequirement}
				disabled={disabled}
			/>
		</Form.Item>
	)
}

EmailField.propTypes = {
	pageFieldsData: shape({
		label: string.isRequired,
		variable: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
			.isRequired,
		type: string.isRequired,
		info: string,
	}).isRequired,
	inputValue: string,
	className: string,
	onChange: func,
	onFocus: func,
	first: bool,
	disabled: bool,
	visible: bool,
	OutsideLabel: string,
	placeHolderRequirement: string,
	form: PropTypes.object,
	setRequesterData: func,
}

EmailField.defaultProps = {
	placeHolderRequirement: '',
	OutsideLabel: '',
	visible: true,
}

export default EmailField
